import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { StringValidator } from 'src/core/validators/shared/string.validator';

@Component({
    selector: 'ca-nice-integration-settings',
    templateUrl: './nice-integration-settings.component.html',
    styleUrls: ['./nice-integration-settings.component.scss'],
    standalone: false
})
export class NiceIntegrationSettingsComponent implements OnInit {
  form: FormGroup;

  private readonly prefix = 'NiceIntegration';
  private readonly clientId = `${this.prefix}.ClientId`;
  private readonly clientSecret = `${this.prefix}.ClientSecret`;
  private readonly sourceName = `${this.prefix}.SourceName`;
  private readonly groupId = `${this.prefix}.GroupId`;
  private readonly accessKey = `${this.prefix}.AccessKey`;
  private readonly accessSecret = `${this.prefix}.AccessSecret`;
  private readonly useMultilingual = `${this.prefix}.UseMultilingual`;
  private readonly languageParameter = `${this.prefix}.LanguageParameter`;
  private readonly languageDictionary = `${this.prefix}.LanguageDictionary`;
  private readonly migrationStartDate = `${this.prefix}.MigrationStartDate`;

  useMultilingualCb = false;

  constructor(
    private fb: FormBuilder,
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService
  ) {

    this.useMultilingualCb = config.getSetting(this.useMultilingual) == 'true';

    this.form = this.fb.group({
      clientId: this.fb.group({
        name: this.clientId,
        value: [
          this.config.getSetting(this.clientId),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      clientSecret: this.fb.group({
        name: this.clientSecret,
        value: [
          this.config.getSetting(this.clientSecret),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      sourceName: this.fb.group({
        name: this.sourceName,
        value: [
          this.config.getSetting(this.sourceName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      groupId: this.fb.group({
        name: this.groupId,
        value: [
          this.config.getSetting(this.groupId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      migrationStartDate: this.fb.group({
        name: this.migrationStartDate,
        value: [
          this.config.getSetting(this.migrationStartDate),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      useMultilingual: this.fb.group({
        name: this.useMultilingual,
        value: [this.config.getSetting(this.useMultilingual)],
      }),
      languageParameter: this.fb.group({
        name: this.languageParameter,
        value: [this.config.getSetting(this.languageParameter)],
      }),
      languageDictionary: this.fb.group({
        name: this.languageDictionary,
        value: [this.config.getSetting(this.languageDictionary)],
      }),
      accessKey: this.fb.group({
        name: this.accessKey,
        value: [
          this.config.getSetting(this.accessKey),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      accessSecret: this.fb.group({
        name: this.accessSecret,
        value: [
          this.config.getSetting(this.accessSecret),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
    });
  }

  ngOnInit(): void { }

  onSubmitForm(eventArgs): void {
    if (!this.form.valid) {
      return;
    }

    const settings: ConfigurationSettingsDto[] = [];

    if (this.form.get('clientId').get('value').value) {
      settings.push({
        settingName: this.form.get('clientId').get('name').value,
        settingValue: this.form.get('clientId').get('value').value,
      });
    }

    if (this.form.get('clientSecret').get('value').value) {
      settings.push({
        settingName: this.form.get('clientSecret').get('name').value,
        settingValue: this.form.get('clientSecret').get('value').value,
      });
    }

    settings.push({
      settingName: this.form.get('sourceName').get('name').value,
      settingValue: this.form.get('sourceName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('groupId').get('name').value,
      settingValue: this.form.get('groupId').get('value').value,
    });

    settings.push({
      settingName: this.form.get('migrationStartDate').get('name').value,
      settingValue: this.form.get('migrationStartDate').get('value').value,
    });

    if (this.form.get('accessKey').get('value').value) {
      settings.push({
        settingName: this.form.get('accessKey').get('name').value,
        settingValue: this.form.get('accessKey').get('value').value,
      });
    }

    if (this.form.get('accessSecret').get('value').value) {
      settings.push({
        settingName: this.form.get('accessSecret').get('name').value,
        settingValue: this.form.get('accessSecret').get('value').value,
      });
    }

    settings.push({
      settingName: this.form.get('useMultilingual').get('name').value,
      settingValue: this.useMultilingualCb.toString(),
    });

    settings.push({
      settingName: this.form.get('languageParameter').get('name').value,
      settingValue: this.form.get('languageParameter').get('value').value,
    });

    settings.push({
      settingName: this.form.get('languageDictionary').get('name').value,
      settingValue: this.form.get('languageDictionary').get('value').value,
    });

    this.settingService.saveSetting(settings).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SuccessfullySaved')
      );
    });
  }

  onUseMultilingualChange(value: boolean) {
    console.log('onUseMultilingualChange triggered');
    if (value) {
      this.form.controls.languageParameter.setValidators([Validators.required]);
      this.form.controls.languageDictionary.setValidators([Validators.required]);
      console.log('languageParameter setValidators as Validators.required');
      console.log('languageDictionary setValidators as Validators.required');
    } else {
      this.form.controls.languageParameter.clearValidators();
      this.form.controls.languageDictionary.clearValidators();
      console.log('languageParameter clearValidators');
      console.log('languageDictionary clearValidators');
    }
    this.form.controls.languageParameter.updateValueAndValidity();
    this.form.controls.languageDictionary.updateValueAndValidity();
  }
}
